import NextLink from 'next/link';
import PropTypes from 'prop-types';

import Avatar from 'shopper/components/Avatar';

import Anchor from 'components/Anchor';

import { getUserAvatarUrl, toUserAvatarAltText } from 'lib/image';
import { toUserProfileLink } from 'lib/links';
import { isGenieUser, isUserAdmin } from 'lib/user';

const sizePropToImageSizeMap = {
  size1: 108,
  size2: 96,
  size3: 85,
  size4: 60,
  size5: 50,
  size6: 36,
};

const UserAvatar = ({
  asLink = false,
  description = null,
  // This does not have the same behavior as `Image` component as it was
  // done after `Avatar` was already being used in production. It would imply
  // to have all `Avatar`s with lazy loading, which could impact performance
  // negatively
  loading,
  size = 'size4',
  userId = null,
  userLevel = null,
  userName = null,
  userPhoto = null,
  userTypeName = null,
  userUsername = null,
  ...rest
}) => {
  const avatar = (
    <Avatar
      admin={isUserAdmin(userTypeName)}
      description={description || toUserAvatarAltText(userName)}
      level={isGenieUser(userId) ? undefined : userLevel}
      loading={loading}
      size={size}
      src={getUserAvatarUrl(userPhoto, sizePropToImageSizeMap[size])}
      {...rest}
    />
  );

  // This check is necessary to avoid wrapping the avatar in an Anchor
  // when the user is a Genie, because Genie profiles no longer exist
  // due to the high number of requests to the backend.
  if (isGenieUser(userId) || !asLink) {
    return avatar;
  }

  return (
    <NextLink href={toUserProfileLink(userUsername)} prefetch={false} passHref>
      <Anchor>{avatar}</Anchor>
    </NextLink>
  );
};

UserAvatar.propTypes = {
  asLink: PropTypes.bool,
  description: PropTypes.string,
  loading: PropTypes.oneOf(['eager', 'lazy']),
  size: PropTypes.string,
  userId: PropTypes.number,
  userLevel: PropTypes.number,
  userName: PropTypes.string,
  userPhoto: PropTypes.string,
  userTypeName: PropTypes.string,
  userUsername: PropTypes.string,
};

export default UserAvatar;
